import React, { useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Row,
  Col,
  Alert,
  Divider,
  Tag,
  App,
  Collapse,
} from 'antd';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined, CloseOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  FlowQuestion,
  FlowQuestionRule,
  FlowRuleAction,
  FlowRuleCondition,
  QualificationFlow,
  QualifyingFlowQuestion,
  QuestionType,
  salesChannelLabel,
} from '../../../../dto/qualifying-flow-models';
import { SalesChannel } from '../../../../dto/model';

const CHANNEL_OPTIONS = Object.values(SalesChannel)
  .filter((c) => c !== SalesChannel.MissingQueue)
  .map((c) => ({ value: c, label: salesChannelLabel[c] }));

const OPERATOR_OPTIONS = [
  { value: '>', label: '>' },
  { value: '<=', label: '<=' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '=', label: '=' },
];

const ROUTE_OPTIONS = Object.values(SalesChannel)
  .filter((c) => c !== SalesChannel.MissingQueue)
  .map((c) => ({ value: c, label: salesChannelLabel[c] }));

type Props = {
  flow: QualificationFlow;
  allQuestions: QualifyingFlowQuestion[];
  onSubmit: (flow: QualificationFlow) => void;
  onCancel: () => void;
  loading?: boolean;
};

// ─── Rule editor for a single question slot ──────────────────────────────────

const RuleEditor = ({
  flowQuestion,
  totalQuestions,
  onChange,
}: {
  flowQuestion: FlowQuestion;
  totalQuestions: number;
  onChange: (updated: FlowQuestion) => void;
}) => {
  const { question, rules } = flowQuestion;
  const isDropdown = question.questionType === QuestionType.Dropdown;

  const updateRule = (idx: number, when: FlowRuleCondition, then: FlowRuleAction) => {
    const updated = rules.map((r, i) => (i === idx ? { when, then } : r));
    onChange({ ...flowQuestion, rules: updated });
  };

  const addRule = () => {
    const newRule: FlowQuestionRule = {
      when: isDropdown ? { optionKey: question.options[0]?.key ?? '' } : { operator: '>', value: 0 },
      then: {},
    };
    onChange({ ...flowQuestion, rules: [...rules, newRule] });
  };

  const removeRule = (idx: number) => {
    onChange({ ...flowQuestion, rules: rules.filter((_, i) => i !== idx) });
  };

  return (
    <div style={{ padding: '8px 0' }}>
      {rules.map((rule, idx) => (
        <Row key={idx} gutter={[4, 4]} align="middle" style={{ marginBottom: 8 }}>
          <Col flex="none" style={{ color: '#999', fontSize: 12 }}>When</Col>
          {isDropdown ? (
            <Col span={7}>
              <Select
                size="small"
                style={{ width: '100%' }}
                value={rule.when.optionKey}
                onChange={(val) => updateRule(idx, { ...rule.when, optionKey: val }, rule.then)}
                options={question.options.map((o) => ({
                  value: o.key,
                  label: `${o.key} — ${o.text?.de ?? o.text?.en ?? o.key}`,
                }))}
                placeholder="Select option"
              />
            </Col>
          ) : (
            <>
              <Col span={3}>
                <Select
                  size="small"
                  style={{ width: '100%' }}
                  value={rule.when.operator}
                  onChange={(val) => updateRule(idx, { ...rule.when, operator: val }, rule.then)}
                  options={OPERATOR_OPTIONS}
                  placeholder="Op"
                />
              </Col>
              <Col span={4}>
                <InputNumber
                  size="small"
                  style={{ width: '100%' }}
                  value={rule.when.value}
                  onChange={(val) => updateRule(idx, { ...rule.when, value: val ?? undefined }, rule.then)}
                  placeholder="Value"
                />
              </Col>
            </>
          )}

          <Col flex="none" style={{ color: '#999', fontSize: 12 }}>Route</Col>
          <Col span={5}>
            <Select
              size="small"
              allowClear
              style={{ width: '100%' }}
              value={rule.then.route}
              onChange={(val) => updateRule(idx, rule.when, { ...rule.then, route: val })}
              options={ROUTE_OPTIONS}
              placeholder="(none)"
            />
          </Col>

          {/* <Col flex="none" style={{ color: '#999', fontSize: 12 }}>Skip</Col>
          <Col span={2}>
            <InputNumber
              size="small"
              min={0}
              style={{ width: '100%' }}
              value={rule.then.skipNext ?? null}
              onChange={(val) => updateRule(idx, rule.when, { ...rule.then, skipNext: val ?? undefined })}
              placeholder="N"
            />
          </Col>

          <Col flex="none" style={{ color: '#999', fontSize: 12 }}>GoTo</Col>
          <Col span={2}>
            <InputNumber
              size="small"
              min={1}
              max={totalQuestions}
              style={{ width: '100%' }}
              value={rule.then.goTo ?? null}
              onChange={(val) => updateRule(idx, rule.when, { ...rule.then, goTo: val ?? undefined })}
              placeholder="#"
            />
          </Col> */}

          <Col flex="none">
            <Button icon={<DeleteOutlined />} size="small" danger type="text" onClick={() => removeRule(idx)} />
          </Col>
        </Row>
      ))}
      <Button icon={<PlusOutlined />} size="small" onClick={addRule} style={{ marginTop: 4 }}>
        Add Rule
      </Button>
    </div>
  );
};

// ─── Sortable question item ───────────────────────────────────────────────────

const SortableQuestionItem = ({
  flowQuestion,
  position,
  totalQuestions,
  onRemove,
  onChange,
}: {
  flowQuestion: FlowQuestion;
  position: number;
  totalQuestions: number;
  onRemove: (feId: string) => void;
  onChange: (updated: FlowQuestion) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: flowQuestion.feId });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    border: '1px solid var(--border-color)',
    borderRadius: 4,
    marginBottom: 8,
    background: 'var(--bg-primary)',
  };

  const { question } = flowQuestion;
  const ruleCount = flowQuestion.rules.length;

  const collapseItems = [
    {
      key: 'rules',
      label: (
        <span style={{ fontSize: 12, color: ruleCount === 0 ? '#999' : undefined }}>
          Rules {ruleCount > 0 ? `(${ruleCount})` : '— none configured'}
        </span>
      ),
      children: (
        <RuleEditor flowQuestion={flowQuestion} totalQuestions={totalQuestions} onChange={onChange} />
      ),
    },
  ];

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', gap: 8 }}>
        <span {...attributes} {...listeners} style={{ cursor: 'grab', color: '#999' }}>
          <HolderOutlined />
        </span>
        <span style={{ color: '#999', fontSize: 12, minWidth: 20 }}>{position}.</span>
        <code style={{ flex: 1, fontSize: 12 }}>{question.questionKey}</code>
        <span style={{ color: '#666', fontSize: 12, flex: 2 }}>{question.questionText?.de}</span>
        <Tag color={question.isActive ? 'green' : 'default'} style={{ marginLeft: 'auto' }}>
          {question.isActive ? 'Active' : 'Inactive'}
        </Tag>
        <Button
          icon={<CloseOutlined />}
          size="small"
          type="text"
          danger
          onClick={() => onRemove(flowQuestion.feId)}
        />
      </div>
      <Collapse ghost size="small" items={collapseItems} style={{ paddingLeft: 36, paddingRight: 8 }} />
    </div>
  );
};

// ─── Main form ────────────────────────────────────────────────────────────────

export const QualificationFlowForm = ({ flow, allQuestions, onSubmit, onCancel, loading }: Props) => {
  const [form] = Form.useForm();
  const [current, setCurrent] = useState<QualificationFlow>({ ...flow, questions: [...flow.questions] });
  const { notification } = App.useApp();

  const assignedQuestionIds = new Set(current.questions.map((fq) => fq.question.id));
  const availableQuestions = allQuestions.filter((q) => !assignedQuestionIds.has(q.id));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = current.questions.findIndex((fq) => fq.feId === active.id);
      const newIndex = current.questions.findIndex((fq) => fq.feId === over.id);
      setCurrent((prev) => ({
        ...prev,
        questions: arrayMove(prev.questions, oldIndex, newIndex),
      }));
    }
  };

  const addQuestion = (q: QualifyingFlowQuestion) => {
    const slot: FlowQuestion = { feId: crypto.randomUUID(), question: q, rules: [] };
    setCurrent((prev) => ({ ...prev, questions: [...prev.questions, slot] }));
  };

  const removeQuestion = (feId: string) => {
    setCurrent((prev) => ({ ...prev, questions: prev.questions.filter((fq) => fq.feId !== feId) }));
  };

  const updateQuestion = (updated: FlowQuestion) => {
    setCurrent((prev) => ({
      ...prev,
      questions: prev.questions.map((fq) => (fq.feId === updated.feId ? updated : fq)),
    }));
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      if (current.questions.length === 0) {
        notification.error({ message: 'Please assign at least one question to the flow' });
        return;
      }
      onSubmit(current);
    } catch {}
  };

  return (
    <div style={{ padding: '16px' }}>
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={14}>
            <Form.Item
              label="Flow Name"
              name="name"
              initialValue={current.name}
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input
                value={current.name}
                onChange={(e) => setCurrent((p) => ({ ...p, name: e.target.value }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Sales Channel"
              name="salesChannel"
              initialValue={current.salesChannel || undefined}
              rules={[{ required: true, message: 'Sales channel is required' }]}
            >
              <Select
                options={CHANNEL_OPTIONS}
                value={current.salesChannel || undefined}
                onChange={(val) => setCurrent((p) => ({ ...p, salesChannel: val }))}
                placeholder="Select channel"
              />
            </Form.Item>
          </Col>
          <Col span={2} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
            <Form.Item label="Active">
              <Switch
                checked={current.isActive}
                onChange={(val) => setCurrent((p) => ({ ...p, isActive: val }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Description" name="description" initialValue={current.description}>
          <Input.TextArea
            rows={2}
            value={current.description}
            onChange={(e) => setCurrent((p) => ({ ...p, description: e.target.value }))}
          />
        </Form.Item>

        {current.isActive && (
          <Alert
            type="info"
            showIcon
            message="Setting this flow as active will deactivate all other flows for the selected sales channel."
            style={{ marginBottom: 16 }}
          />
        )}

        <Divider orientation="left">Assigned Questions (drag to reorder)</Divider>
        <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
          Expand a question row to configure its decision rules: Route.
        </div>

        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={current.questions.map((fq) => fq.feId)}
            strategy={verticalListSortingStrategy}
          >
            {current.questions.length === 0 ? (
              <div style={{ color: '#999', marginBottom: 12 }}>No questions assigned yet.</div>
            ) : (
              current.questions.map((fq, idx) => (
                <SortableQuestionItem
                  key={fq.feId}
                  flowQuestion={fq}
                  position={idx + 1}
                  totalQuestions={current.questions.length}
                  onRemove={removeQuestion}
                  onChange={updateQuestion}
                />
              ))
            )}
          </SortableContext>
        </DndContext>

        <Divider orientation="left">Available Questions</Divider>

        <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 4 }}>
          {availableQuestions.length === 0 ? (
            <div style={{ padding: '12px 16px', color: '#999' }}>All questions are assigned.</div>
          ) : (
            availableQuestions.map((q) => (
              <div
                key={q.feId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--border-color)',
                  gap: 8,
                  cursor: 'pointer',
                }}
                onClick={() => addQuestion(q)}
              >
                <code style={{ fontSize: 12, flex: 1 }}>{q.questionKey}</code>
                <span style={{ fontSize: 12, flex: 2 }}>{q.questionText?.de}</span>
                <Tag color={q.isActive ? 'green' : 'default'}>{q.isActive ? 'Active' : 'Inactive'}</Tag>
                <Button size="small" type="link">
                  + Add
                </Button>
              </div>
            ))
          )}
        </div>
      </Form>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button type="primary" loading={loading} onClick={handleSubmit}>
          Save
        </Button>
      </div>
    </div>
  );
};
